<template>
    <b-modal no-close-on-backdrop id="recoveryPassword" @hide="clearForm" :hide-footer="true" class="recovery-modal" modal-class="recovery-modal-wrapper" content-class="recovery-modal-content" header-class="recovery-modal-header" body-class="recovery-modal-body">
        <template slot="modal-header">
            <div class="recovery-header">
                <div class="recovery-header-icon">
                    <i class="fas fa-lock"></i>
                </div>
                <h4 class="recovery-title">{{ $t('str.modal.recovery.password.title') }}</h4>
            </div>
            <a class="recovery-close" @click="$bvModal.hide('recoveryPassword')">
                <i class="fas fa-times"></i>
            </a>
        </template>

        <!-- Step indicator -->
        <div class="recovery-steps">
            <div class="recovery-step" :class="{ active: step === 1, completed: step > 1 }">
                <div class="step-circle">
                    <i v-if="step > 1" class="fas fa-check"></i>
                    <span v-else>1</span>
                </div>
                <span class="step-label">{{ $t('str.modal.email.step') }}</span>
            </div>
            <div class="step-connector" :class="{ active: step > 1 }"></div>
            <div class="recovery-step" :class="{ active: step === 2, completed: step > 2 }">
                <div class="step-circle">
                    <i v-if="step > 2" class="fas fa-check"></i>
                    <span v-else>2</span>
                </div>
                <span class="step-label">{{ $t('str.modal.check.code.step') }}</span>
            </div>
            <div class="step-connector" :class="{ active: step > 2 }"></div>
            <div class="recovery-step" :class="{ active: step === 3 }">
                <div class="step-circle">
                    <span>3</span>
                </div>
                <span class="step-label">{{ $t('str.modal.finished.step') }}</span>
            </div>
        </div>

        <!-- Step 1: Email -->
        <div v-if="step === 1" class="recovery-form">
            <div class="form-group-dark">
                <label class="form-label-dark">{{ $t('str.recovery.password.email.field') }}</label>
                <div class="input-wrapper">
                    <i class="fas fa-envelope input-icon"></i>
                    <input
                        autocomplete="off"
                        type="text"
                        class="form-control-dark"
                        v-model="data.email"
                        :placeholder="$t('str.recovery.password.email.placeholder')"
                        @keydown.enter="handleGenerateCodePassword"
                    />
                </div>
            </div>
            <div class="recovery-actions">
                <button :disabled="!isValidEmail(data.email)" @click="handleGenerateCodePassword" type="submit" class="btn-recovery-primary">
                    <i v-if="isLoading === true" class="fas fa-spinner fa-pulse me-2"></i>
                    {{ $t('str.btn.next') }}
                    <i v-if="!isLoading" class="fas fa-arrow-right ms-2"></i>
                </button>
            </div>
        </div>

        <!-- Step 2: Code & New Password -->
        <div v-if="step === 2" class="recovery-form">
            <div class="form-group-dark">
                <label class="form-label-dark" for="codeField">{{ $t('str.recovery.password.code.field') }}</label>
                <div class="input-wrapper">
                    <i class="fas fa-key input-icon"></i>
                    <input
                        autocomplete="off"
                        type="text"
                        id="codeField"
                        v-mask="'XXXXXXXX'"
                        @input="transformUpperCase"
                        v-model="data.code"
                        class="form-control-dark code-input"
                        :placeholder="$t('str.recovery.password.code.placeholder')"
                    />
                </div>
            </div>
            <div class="form-group-dark">
                <label class="form-label-dark" for="passwordInput">{{ $t('str.recovery.password.newpassword.field') }}</label>
                <div class="input-wrapper">
                    <i class="fas fa-lock input-icon"></i>
                    <input
                        autocomplete="new-password"
                        id="passwordInput"
                        class="form-control-dark"
                        v-model="data.password"
                        v-bind:type="showPassword ? 'text' : 'password'"
                        :placeholder="$t('str.recovery.password.newpassword.placeholder')"
                    />
                    <span class="toggle-password" v-on:click="showPassword = !showPassword">
                        <i v-bind:class="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                    </span>
                </div>
            </div>
            <div class="form-group-dark">
                <label class="form-label-dark" for="confirmPasswordInput">{{ $t('str.recovery.password.confirm.newpassword.field') }}</label>
                <div class="input-wrapper">
                    <i class="fas fa-lock input-icon"></i>
                    <input
                        autocomplete="new"
                        id="confirmPasswordInput"
                        class="form-control-dark"
                        v-model="data.passwordConfirm"
                        v-bind:type="showConfirmPassword ? 'text' : 'password'"
                        :placeholder="$t('str.recovery.password.newpassword.placeholder')"
                    />
                    <span class="toggle-password" v-on:click="showConfirmPassword = !showConfirmPassword">
                        <i v-bind:class="showConfirmPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
                    </span>
                </div>
            </div>
            <div class="recovery-actions">
                <button :disabled="checkEnableButton()" v-on:click="checkPasswordStep" type="submit" class="btn-recovery-primary">
                    <i v-if="isLoading === true" class="fas fa-spinner fa-pulse me-2"></i>
                    {{ $t('str.btn.next') }}
                    <i v-if="!isLoading" class="fas fa-arrow-right ms-2"></i>
                </button>
            </div>
        </div>

        <!-- Step 3: Success -->
        <div v-if="step === 3" class="recovery-form recovery-success">
            <div class="success-icon-wrapper">
                <div class="success-icon">
                    <i class="fas fa-check-circle"></i>
                </div>
            </div>
            <h5 class="success-message">{{ $t('str.password.success.changed') }}</h5>
            <div class="recovery-actions">
                <button v-on:click="$bvModal.hide('recoveryPassword')" type="submit" class="btn-recovery-primary">
                    {{ $t('str.btn.close') }}
                </button>
            </div>
        </div>

        <notifications group="bottom-right" position="bottom right" :speed="500" />
    </b-modal>
</template>

<script>
import Controller from './CrtRecoveryPassword.vue'
export default {
    props: {
        email: {
            type: String,
            default: () => '',
        },
    },
    data() {
        return {
            isLoading: false,
            isCheckEmail: false,
            step: 1,
            showPassword: false,
            showConfirmPassword: false,
            data: {
                email: '',
                password: '',
                passwordConfirm: '',
                code: '',
                systemUser: '',
            },
        }
    },
    methods: Controller.methods,
}
</script>

<style lang="scss">
.recovery-modal-wrapper {
    .modal-dialog {
        max-width: 480px;
    }
}

.recovery-modal-content {
    background-color: #1d1c20 !important;
    border: 1px solid rgba(255, 255, 255, 0.06) !important;
    border-radius: 16px !important;
    box-shadow: 0 25px 60px rgba(0, 0, 0, 0.6) !important;
    overflow: hidden;
}

.recovery-modal-header {
    background-color: #1d1c20 !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06) !important;
    padding: 24px 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: space-between !important;
}

.recovery-modal-body {
    background-color: #1d1c20 !important;
    padding: 24px 28px 32px !important;
}
</style>

<style lang="scss" scoped>
.recovery-header {
    display: flex;
    align-items: center;
    gap: 14px;
}

.recovery-header-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--app-theme), rgba(var(--app-theme-rgb, 220, 53, 69), 0.7));
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 16px;
}

.recovery-title {
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.01em;
}

.recovery-close {
    color: rgba(255, 255, 255, 0.4);
    font-size: 18px;
    cursor: pointer;
    transition: color 0.2s;
    padding: 4px;

    &:hover {
        color: #fff;
    }
}

// Step indicator
.recovery-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 32px;
    gap: 0;
}

.recovery-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    position: relative;

    .step-circle {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 600;
        background: #2a292e;
        color: rgba(255, 255, 255, 0.35);
        border: 2px solid rgba(255, 255, 255, 0.08);
        transition: all 0.3s ease;
    }

    .step-label {
        font-size: 11px;
        color: rgba(255, 255, 255, 0.3);
        font-weight: 500;
        white-space: nowrap;
        transition: color 0.3s ease;
    }

    &.active {
        .step-circle {
            background: var(--app-theme);
            color: #fff;
            border-color: var(--app-theme);
            box-shadow: 0 0 20px rgba(var(--app-theme-rgb, 220, 53, 69), 0.35);
        }

        .step-label {
            color: #fff;
        }
    }

    &.completed {
        .step-circle {
            background: var(--app-theme);
            color: #fff;
            border-color: var(--app-theme);
        }

        .step-label {
            color: rgba(255, 255, 255, 0.6);
        }
    }
}

.step-connector {
    width: 48px;
    height: 2px;
    background: rgba(255, 255, 255, 0.08);
    margin: 0 8px;
    margin-bottom: 22px;
    border-radius: 1px;
    transition: background 0.3s ease;

    &.active {
        background: var(--app-theme);
    }
}

// Form styles
.recovery-form {
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.form-group-dark {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-label-dark {
    color: rgba(255, 255, 255, 0.7);
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.02em;
}

.input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
}

.input-icon {
    position: absolute;
    left: 14px;
    color: rgba(255, 255, 255, 0.25);
    font-size: 14px;
    z-index: 1;
    pointer-events: none;
}

.form-control-dark {
    width: 100%;
    background: #2a292e;
    border: 1px solid rgba(255, 255, 255, 0.06);
    border-radius: 10px;
    color: #fff;
    padding: 12px 14px 12px 42px;
    font-size: 14px;
    transition: all 0.2s ease;
    outline: none;

    &::placeholder {
        color: rgba(255, 255, 255, 0.2);
    }

    &:focus {
        border-color: var(--app-theme);
        box-shadow: 0 0 0 3px rgba(var(--app-theme-rgb, 220, 53, 69), 0.15);
        background: #2e2d32;
    }

    &.code-input {
        letter-spacing: 4px;
        font-weight: 600;
        font-family: monospace;
        text-align: center;
        padding-left: 42px;
        padding-right: 14px;
    }
}

.toggle-password {
    position: absolute;
    right: 14px;
    color: rgba(255, 255, 255, 0.3);
    cursor: pointer;
    font-size: 14px;
    transition: color 0.2s;
    z-index: 1;

    &:hover {
        color: rgba(255, 255, 255, 0.7);
    }
}

// Actions
.recovery-actions {
    display: flex;
    justify-content: center;
    margin-top: 8px;
}

.btn-recovery-primary {
    background: var(--app-theme);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 12px 40px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    gap: 4px;
    letter-spacing: 0.02em;

    &:hover:not(:disabled) {
        filter: brightness(1.15);
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(var(--app-theme-rgb, 220, 53, 69), 0.35);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
    }

    &:disabled {
        opacity: 0.35;
        cursor: not-allowed;
    }
}

// Success state
.recovery-success {
    align-items: center;
    padding: 16px 0 8px;
}

.success-icon-wrapper {
    margin-bottom: 8px;
}

.success-icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: rgba(50, 169, 50, 0.12);
    display: flex;
    align-items: center;
    justify-content: center;

    i {
        font-size: 36px;
        color: #32a932;
    }
}

.success-message {
    color: #fff;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    margin: 0;
}
</style>
